import * as BaseDialog from "@/components/ui/dialog";
import { MutableRefObject, ReactNode, useCallback, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";

type DialogProps = {
    title?: ReactNode;
    trigger?: ReactNode;
    titleClass?: string;
    children?: ReactNode;
    scrollContainerRef?: MutableRefObject<HTMLElement>;
    scrollContainerClass?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    onOpenAutoFocus?: (event: Event) => void;
};

const Dialog = ({
    scrollContainerRef,
    scrollContainerClass,
    children,
    titleClass,
    title,
    trigger,
    open: openProp,
    onOpenChange: onOpenChangeProp,
    onOpenAutoFocus,
}: DialogProps) => {
    const [localOpen, localSetOpen] = useState(false);
    const open = openProp ?? localOpen;

    const handleOpenChanged = useCallback((open: boolean) => {
        localSetOpen(open);
        onOpenChangeProp?.(open);
    }, [onOpenChangeProp]);

    return (
        <BaseDialog.Dialog
            onOpenChange={handleOpenChanged}
            open={open}
        >
            <BaseDialog.DialogTrigger asChild>
                {trigger}
            </BaseDialog.DialogTrigger>
            <BaseDialog.DialogContent
                aria-describedby={undefined}
                className="flex flex-col max-w-3xl w-[90%] max-h-[95dvh] px-2 rounded-lg"
                onOpenAutoFocus={onOpenAutoFocus}
            >
                <div className="h-full flex flex-col overflow-y-auto overscroll-contain">
                    <div
                        ref={element => {
                            if (!scrollContainerRef) return;
                            scrollContainerRef.current = element;
                        }}
                        className={cn(
                            "h-full px-1 scrollbar-stable-both-edges flex flex-col overflow-y-auto overscroll-contain scrollbar scrollbar-track-transparent scrollbar-thumb-step-current text-left",
                            scrollContainerClass
                        )}
                    >
                        <BaseDialog.DialogHeader>
                            {title
                                ? <BaseDialog.DialogTitle className={cn("mb-1 text-xl text-step-current", titleClass)}>{title}</BaseDialog.DialogTitle>
                                : <VisuallyHidden><BaseDialog.DialogTitle></BaseDialog.DialogTitle></VisuallyHidden>
                            }
                        </BaseDialog.DialogHeader>
                        {children}
                    </div>
                </div>
            </BaseDialog.DialogContent>
        </BaseDialog.Dialog>
    );
};

export { Dialog };